import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PlayerPickerSalary from './playerpicker/PlayerPickerSalary';
import LineupSalary from './lineup/LineupSalary';
import { ConfirmEntryModal } from '../modals/ConfirmEntryModal';
import { handleSubmit } from '../utils/draftUtils';

const DraftModule = ({ contest }) => {
  // Determine total players based on format
  const getInitialLineup = (format) => {
    const totalPlayers = {
      'Pick2': 2,
      'Pick3': 3,
      'Pick4': 4,
      'Pick5': 5,
      'H2H': 2, // Assuming H2H is similar to Pick2
      'LL': 2,  // Assuming LL is similar to Pick2
    }[format] || 2; // Default to 2 if format not found

    return [
      { position: 'Cpt', player: null },
      ...Array(totalPlayers - 1).fill({ position: 'Flex', player: null })
    ];
  };

  const [lineup, setLineup] = useState(() => getInitialLineup(contest.format));
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const salaryCap = contest.salaryCap;

  // Calculate derived values
  const selectedPlayers = lineup.map(slot => slot.player).filter(Boolean);
  const isRosterFull = lineup.every(slot => slot.player);
  const totalSalary = selectedPlayers.reduce((total, player) => total + (player?.salary || 0), 0);
  const remainingSalary = salaryCap - totalSalary;

  const handlePlayerSelect = (player) => {
    if (lineup.every(slot => slot.player)) {
      return;
    }

    setLineup((prevLineup) => {
      const updatedLineup = [...prevLineup];

      const cptIndex = updatedLineup.findIndex(slot => slot.position === 'Cpt' && !slot.player);
      if (cptIndex !== -1) {
        updatedLineup[cptIndex] = { ...updatedLineup[cptIndex], player };
        return updatedLineup;
      }

      const flexIndex = updatedLineup.findIndex(slot => slot.position === 'Flex' && !slot.player);
      if (flexIndex !== -1) {
        updatedLineup[flexIndex] = { ...updatedLineup[flexIndex], player };
        return updatedLineup;
      }

      return prevLineup;
    });
  };

  const handlePlayerRemove = (position) => {
    setLineup((prevLineup) => {
      const updatedLineup = [...prevLineup];
      updatedLineup[position] = { position: updatedLineup[position].position, player: null };
      return updatedLineup;
    });
  };

  const handleClear = () => {
    setLineup(getInitialLineup(contest.format));
  };

  const handleConfirmSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    setIsSubmitting(true); // Show loading spinner
    setIsSuccess(false); // Reset success state
    let result = null;
    
    try {
      // Define the success handler here
      const handleSuccess = () => {
        setIsSuccess(true); // Show success checkmark
        // Close modal after a delay
        setTimeout(() => {
          setShowConfirmation(false);
        }, 800); // Delay to show checkmark
        
        // Optionally, trigger page reload or state update after modal closes + delay
        setTimeout(() => {
          window.location.reload(); // Or navigate, or update state
        }, 1500); // Longer delay for reload
      };
      
      // Call handleSubmit, passing the success handler
      result = await handleSubmit(lineup, contest, handleSuccess);
      
      // If handleSubmit itself indicates failure (e.g., validation error or API error)
      if (!result.success) {
        // Show alert with the specific error message from handleSubmit
        alert(result.message); 
        // Ensure success state is false if API call failed after success handler was defined
        // but before it was called (though handleSubmit structure prevents this)
        setIsSuccess(false); 
      }

    } catch (error) {
      // This catch block is less likely to be hit now since handleSubmit catches its own errors,
      // but keep it for unexpected issues.
      console.error("Unexpected error during submission process:", error);
      alert("An unexpected error occurred during submission. Please try again.");
      setIsSuccess(false); // Ensure success state is false
    } finally {
      // IMPORTANT: Always reset submitting state, regardless of success or failure
      // unless success animation/modal close is handling it.
      // Check if it was a success handled by handleSuccess callback
      if (!result || !result.success) {
         setIsSubmitting(false);
      }
      // If successful, isSubmitting might remain true briefly for the checkmark animation,
      // handleSuccess callback will manage closing the modal and resetting state via reload/nav.
    }
  };

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setShowConfirmation(false);
    }
  };

  const handleOpenSubmitModal = () => {
    setShowConfirmation(true);
  };

  // Calculate submit disabled state here
  const isSubmitDisabled = !isRosterFull || remainingSalary < 0;

  // Add this for debugging
  // console.log('Current state:', {
  //   isRosterFull,
  //   remainingSalary,
  //   totalSalary,
  //   lineup,
  //   isSubmitDisabled: !isRosterFull || remainingSalary < 0
  // });

  return (
    <div className="module-container">
      <PlayerPickerSalary 
        players={contest.players}
        onPlayerSelect={handlePlayerSelect}
        selectedPlayers={selectedPlayers}
        disabled={isRosterFull}
        sport={contest.sport}
      />
      
      <div className="lineup-and-buttons-container">
        <LineupSalary 
          lineup={lineup}
          salaryCap={salaryCap}
          onPlayerRemove={handlePlayerRemove}
          totalPlayers={lineup.length}
          format={contest.format}
          sport={contest.sport}
        />

        <div className="lineup-action-buttons-container"> 
          <button
            className="submit-btn"
            onClick={handleOpenSubmitModal}
            disabled={isSubmitDisabled}
          >
            Submit
          </button>
          <button 
            className="clear-btn" 
            onClick={handleClear}
          >
            Clear
          </button>
        </div>

      </div>

      <ConfirmEntryModal
        show={showConfirmation}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSubmit}
        contest={contest}
        lineup={lineup}
        totalSalary={totalSalary}
        salaryCap={salaryCap}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
      />
    </div>
  );
};

DraftModule.propTypes = {
  contest: PropTypes.shape({
    format: PropTypes.string.isRequired,
    salaryCap: PropTypes.number.isRequired,
    sport: PropTypes.string.isRequired,
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      salary: PropTypes.number.isRequired,
      sportSpecificData: PropTypes.shape({
        chipCount: PropTypes.number,
        odds: PropTypes.number,
        rating: PropTypes.number,
        seed: PropTypes.number,
      })
    })).isRequired,
    entryFee: PropTypes.number.isRequired,
    adminFee: PropTypes.number.isRequired,
    tournamentName: PropTypes.string.isRequired
  }).isRequired
};

export default DraftModule;
